import React from 'react';
import { IntervalTemplate } from 'api/report/types';
import {
    IntervalEntryDraft,
    isEmptyIntervalEntryDraft,
} from 'report/logic/interval/editing';
import { useIntervalDraft } from './IntervalDraftContext';
import { EntryEditorProps } from './types';

type DialogState =
    | { type: 'closed' }
    | { type: 'open'; entryID: string }
    | { type: 'closing'; editor: EntryEditorProps };

interface Options {
    readOnly: boolean;
    disabled: boolean;
}

export function useIntervalEditor({ readOnly, disabled }: Options) {
    const {
        sourceReport,
        draft,
        originalEntries,
        entryValidation,
        addEntry,
        updateEntry,
        deleteEntry,
    } = useIntervalDraft();
    const [dialogState, setDialogState] = React.useState<DialogState>({
        type: 'closed',
    });
    const { selectedEntry, isNew = false } = React.useMemo(() => {
        if (dialogState.type !== 'open') return {};
        for (const day of draft.days) {
            const selectedEntry = day.entries.find(
                entry => entry.uuid === dialogState.entryID
            );
            if (selectedEntry) {
                return {
                    selectedEntry,
                    isNew: !originalEntries.some(
                        entry => entry.uuid === selectedEntry.uuid
                    ),
                };
            }
        }
        return {};
    }, [draft.days, originalEntries, dialogState]);
    const { entryKinds } = sourceReport.template.data as IntervalTemplate;

    React.useEffect(() => {
        setDialogState({ type: 'closed' });
    }, [sourceReport.data]);

    const content = React.useMemo<EntryEditorProps | null>(() => {
        if (dialogState.type === 'closing') return dialogState.editor;
        if (!selectedEntry) return null;

        const beginClosing = () => {
            // Retain the fields until onExited, even if the row is removed now.
            setDialogState({
                type: 'closing',
                editor,
            });
        };
        const editor: EntryEditorProps = {
            draft: selectedEntry,
            validation: entryValidation[selectedEntry.uuid],
            isNew,
            readOnly,
            disabled,
            entryKinds,
            timeZone: draft.timeZone,
            onChange: update => {
                if (!disabled) updateEntry(selectedEntry.uuid, update);
            },
            onDelete: () => {
                if (disabled) return;
                beginClosing();
                deleteEntry(selectedEntry.uuid);
            },
            onClose: () => {
                beginClosing();
                if (
                    !disabled &&
                    isNew &&
                    isEmptyIntervalEntryDraft(selectedEntry)
                ) {
                    deleteEntry(selectedEntry.uuid);
                }
            },
        };
        return editor;
    }, [
        dialogState,
        selectedEntry,
        isNew,
        entryValidation,
        entryKinds,
        draft.timeZone,
        readOnly,
        disabled,
        updateEntry,
        deleteEntry,
    ]);

    const openEntry = (entryID: string) => {
        if (selectedEntry && entryID !== selectedEntry.uuid) {
            content?.onClose();
        }
        setDialogState({ type: 'open', entryID });
    };

    const selectEntry = (entry: IntervalEntryDraft) => {
        if (entry.uuid === selectedEntry?.uuid) {
            content?.onClose();
        } else {
            openEntry(entry.uuid);
        }
    };

    const handleAddEntry = (date: string) => {
        if (disabled) return;
        const entryID = addEntry(date);
        if (entryID) openEntry(entryID);
    };

    const onExited = () => {
        // An earlier exit must not clear an editor that has since reopened.
        setDialogState(current =>
            current === dialogState && current.type === 'closing'
                ? { type: 'closed' }
                : current
        );
    };

    return {
        content,
        isOpen: dialogState.type === 'open',
        selectedEntry,
        selectEntry,
        openEntry,
        addEntry: handleAddEntry,
        onExited,
    };
}
